import * as React from 'react';
import { ArrowRight, Loader2, Route } from 'lucide-react';
import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Combobox } from 'components/ui/combobox';
import { RouteSearchCombobox } from './RouteSearchCombobox';
import { RouteGeoSuggestion } from '../api';

export interface RouteSearchPanelProps {
  onSuggest: (side: 'origin' | 'destination', q: string) => Promise<RouteGeoSuggestion[]>;
  onFetchVehicleTypes: (
    origin: RouteGeoSuggestion,
    destination: RouteGeoSuggestion
  ) => Promise<string[]>;
  onOpenAnalysis: (params: {
    origin: RouteGeoSuggestion;
    destination: RouteGeoSuggestion;
    vehicleType: string;
  }) => void;
  loading?: boolean;
}

export const RouteSearchPanel: React.FC<RouteSearchPanelProps> = ({
  onSuggest,
  onFetchVehicleTypes,
  onOpenAnalysis,
  loading = false,
}) => {
  const [origin, setOrigin] = React.useState<RouteGeoSuggestion | null>(null);
  const [destination, setDestination] = React.useState<RouteGeoSuggestion | null>(null);
  const [vehicleType, setVehicleType] = React.useState<string>('');
  const [vehicleOptions, setVehicleOptions] = React.useState<string[]>([]);
  const [loadingVehicles, setLoadingVehicles] = React.useState(false);

  const suggestOrigin = React.useCallback((q: string) => onSuggest('origin', q), [onSuggest]);
  const suggestDestination = React.useCallback((q: string) => onSuggest('destination', q), [onSuggest]);

  // Load vehicle types whenever a full origin+destination pair is selected.
  React.useEffect(() => {
    if (!origin || !destination) {
      setVehicleOptions([]);
      setVehicleType('');
      return;
    }
    let cancelled = false;
    setLoadingVehicles(true);
    onFetchVehicleTypes(origin, destination)
      .then(types => {
        if (cancelled) return;
        setVehicleOptions(types);
        // Clear a previously-picked type that no longer exists for this route.
        setVehicleType(prev => (types.includes(prev) ? prev : ''));
      })
      .catch(() => {
        if (!cancelled) {
          setVehicleOptions([]);
          setVehicleType('');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingVehicles(false);
      });
    return () => {
      cancelled = true;
    };
  }, [origin, destination, onFetchVehicleTypes]);

  const canSearch = !!origin && !!destination && !!vehicleType && !loading;

  const handleSearch = () => {
    if (!origin || !destination || !vehicleType) return;
    onOpenAnalysis({ origin, destination, vehicleType });
  };

  const vehicleComboOptions = vehicleOptions.map(v => ({ value: v, label: v }));
  const bothSelected = !!origin && !!destination;

  return (
    <Card className="relative overflow-hidden border-blue-100 bg-gradient-to-br from-sky-50 via-white to-white p-5 shadow-sm">
      {/* soft decorative glow — Skyscanner/Flexport-style search hero, kept subtle */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blue-200/30 blur-3xl" />

      <div className="relative mb-4 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600/10 text-blue-700 ring-1 ring-blue-200">
          <Route className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-800">Search Route</h3>
        </div>
      </div>

      <div className="relative flex flex-col lg:flex-row lg:items-end gap-4">
        {/* Origin → Destination */}
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] sm:items-end gap-3">
          <div className="space-y-1 min-w-0">
            <label className="text-sm font-medium text-gray-700">Origin</label>
            <RouteSearchCombobox
              value={origin}
              onChange={setOrigin}
              onSearch={suggestOrigin}
              placeholder="Search city, district, location or state"
            />
          </div>

          <div className="hidden sm:flex items-center justify-center pb-1.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-blue-600 ring-1 ring-blue-200 shadow-sm">
              <ArrowRight className="h-3.5 w-3.5" />
            </div>
          </div>

          <div className="space-y-1 min-w-0">
            <label className="text-sm font-medium text-gray-700">Destination</label>
            <RouteSearchCombobox
              value={destination}
              onChange={setDestination}
              onSearch={suggestDestination}
              placeholder="Search city, district, location or state"
            />
          </div>
        </div>

        {/* Vehicle type */}
        <div className="space-y-1 w-full lg:w-56">
          <label className="text-sm font-medium text-gray-700">Vehicle Type</label>
          <Combobox
            options={vehicleComboOptions}
            value={vehicleType}
            onValueChange={val => setVehicleType(val)}
            placeholder={
              !bothSelected
                ? 'Pick origin & destination'
                : loadingVehicles
                ? 'Loading…'
                : vehicleComboOptions.length
                ? '-- select --'
                : 'No types for route'
            }
            disabled={!bothSelected || loadingVehicles || vehicleComboOptions.length === 0}
            buttonClassName="h-10 w-full px-3 text-sm"
          />
        </div>

        {/* Action */}
        <Button onClick={handleSearch} disabled={!canSearch} className="h-10 w-full lg:w-auto">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Open Route Analysis
        </Button>
      </div>
    </Card>
  );
};

export default RouteSearchPanel;
